import { useEffect, useRef, useState } from 'react';

import type { VideoBackdropOptions } from '../model/types';

export const useVideoBackdrop = ({ remoteVideoRef, isRemoteVideoActive }: VideoBackdropOptions) => {
	const remoteBackdropRef = useRef<HTMLVideoElement | null>(null);
	const remoteFrameRef = useRef<HTMLDivElement | null>(null);
	// Отмечаем, когда формат видео не совпадает с рамкой (letterbox) — тогда включаем блюр фона
	const [hasLetterbox, setHasLetterbox] = useState(false);

	useEffect(() => {
		if (!isRemoteVideoActive) {
			setHasLetterbox(false);
		}
	}, [isRemoteVideoActive]);

	useEffect(() => {
		// Поддерживаем две вещи:
		// 1) дублируем стрим в фон для блюра
		// 2) считаем letterbox, чтобы включать/выключать фон только когда есть пустые поля
		const remoteVideo = remoteVideoRef?.current;
		const backdropVideo = remoteBackdropRef.current;
		const frame = remoteFrameRef.current;
		if (!remoteVideo || !backdropVideo || !frame) return;

		const syncStream = () => {
			if (remoteVideo.srcObject) {
				if (backdropVideo.srcObject !== remoteVideo.srcObject) {
					backdropVideo.srcObject = remoteVideo.srcObject;
				}
			} else if (backdropVideo.srcObject) {
				backdropVideo.srcObject = null;
			}
		};

		const recomputeLetterbox = () => {
			if (!isRemoteVideoActive) {
				setHasLetterbox(false);
				return;
			}

			const { videoWidth, videoHeight } = remoteVideo;
			const { clientWidth, clientHeight } = frame;
			if (!videoWidth || !videoHeight || !clientWidth || !clientHeight) {
				setHasLetterbox(false);
				return;
			}

			const videoRatio = videoWidth / videoHeight;
			const frameRatio = clientWidth / clientHeight;
			const letterboxed = Math.abs(videoRatio - frameRatio) > 0.01;
			setHasLetterbox(letterboxed);
		};

		const handleVideoChange = () => {
			syncStream();
			recomputeLetterbox();
		};

		syncStream();
		recomputeLetterbox();

		remoteVideo.addEventListener('loadedmetadata', handleVideoChange);
		remoteVideo.addEventListener('play', handleVideoChange);
		remoteVideo.addEventListener('resize', handleVideoChange);
		window.addEventListener('resize', recomputeLetterbox);

		const resizeObserver =
			typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(recomputeLetterbox);
		resizeObserver?.observe(frame);

		return () => {
			remoteVideo.removeEventListener('loadedmetadata', handleVideoChange);
			remoteVideo.removeEventListener('play', handleVideoChange);
			remoteVideo.removeEventListener('resize', handleVideoChange);
			window.removeEventListener('resize', recomputeLetterbox);
			resizeObserver?.disconnect();
		};
	}, [isRemoteVideoActive, remoteVideoRef]);

	return { remoteBackdropRef, remoteFrameRef, hasLetterbox };
};
